import { useEffect } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { IContactList } from '@/types';
import axiosClient from '@/utils/axiosClient';
import Drawer from '../Modals/Drawer';

const schema = z.object({
  name: z.string().nonempty({ message: 'Name is required' }),
  description: z.string().nonempty({ message: 'Description is required' }),
});

type FormData = z.infer<typeof schema>;

function mapDefaultValues(campaign: IContactList) {
  return {
    name: campaign.name,
    description: campaign.description,
  };
}

interface ContactListDrawerProps {
  contactList: IContactList;
}

const ContactListDrawer = NiceModal.create(({ contactList }: ContactListDrawerProps) => {
  const modal = useModal('contact-list-drawer');
  const queryClient = useQueryClient();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  useEffect(() => {
    if (contactList) {
      const formValues = mapDefaultValues(contactList);
      form.reset(formValues as any);
    }
  }, [contactList]);

  const onSubmit = async (data: FormData) => {
    try {
      if (contactList) {
        await axiosClient.put(`v1/contact_lists/${contactList.id}`, data);
        queryClient.invalidateQueries(`v1/contact_lists/${contactList.id}`);
      } else {
        await axiosClient.post('v1/contact_lists', data);
      }

      queryClient.invalidateQueries('v1/contact_lists');
      modal.hide();
    } catch (error) {
      console.error('Error saving contact list:', error);
    }
  };

  return (
    <Drawer modal={modal} title="Contact List">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col justify-center gap-4 p-4">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="relative inline-flex items-center justify-end w-full gap-2 mt-2">
            <Button type="submit" disabled={form.formState.isSubmitting} className="w-full sm:w-fit">
              <Save />
              {form.formState.isSubmitting ? 'Saving...' : 'Save List'}
            </Button>
          </div>
        </form>
      </Form>
    </Drawer>
  );
});

export default ContactListDrawer;
